import React from "react";
import styles from "../Map.module.css";

export type ItemType = "landmark" | "food";

export interface CityItem {
  id: number;
  label: string;
  type: ItemType;
}

export interface CityEntry {
  id: number;
  city: string;
  items: CityItem[];
}

interface SimpleHashMapEngineProps {
  entries: CityEntry[];
  highlightCityId: number | null;
  highlightItemIds: number[];
  onDeleteItem: (cityId: number, itemId: number) => void;
}

const SimpleHashMapEngine: React.FC<SimpleHashMapEngineProps> = ({
  entries,
  highlightCityId,
  highlightItemIds,
  onDeleteItem,
}) => {
  if (entries.length === 0) {
    return (
      <p className={styles.emptyState}>
        No data yet. Click <strong>Generate Random</strong> or add your own
        cities and items.
      </p>
    );
  }

  return (
    <div className={styles.mapGraphView}>
      <div className={styles.mapGraphHeaderRow}>
        <div className={styles.mapGraphHeaderCell}>City (Key)</div>
        <div className={styles.mapGraphHeaderSpacer} />
        <div className={styles.mapGraphHeaderCell}>
          Landmarks &amp; Foods (Values)
        </div>
      </div>

      {entries.map((entry) => {
        const keyHighlighted = entry.id === highlightCityId;
        const anyHighlightedItem = entry.items.some((item) =>
          highlightItemIds.includes(item.id)
        );

        return (
          <div key={entry.id} className={styles.mapGraphRow}>
            <div
              className={[
                styles.mapNode,
                styles.mapNodeKey,
                keyHighlighted ? styles.mapNodeHighlighted : "",
              ].join(" ")}
            >
              {entry.city}
            </div>

            <div
              className={[
                styles.mapConnection,
                anyHighlightedItem ? styles.mapConnectionVisible : "",
              ].join(" ")}
            />

            <div
              className={[
                styles.mapNode,
                styles.mapNodeValue,
                anyHighlightedItem ? styles.mapNodeHighlighted : "",
              ].join(" ")}
            >
              {entry.items.length === 0 ? (
                <span className={styles.emptyState}>No items yet</span>
              ) : (
                <ul className={styles.valueItemsList}>
                  {entry.items.map((item) => {
                    const itemHighlighted = highlightItemIds.includes(item.id);
                    return (
                      <li
                        key={item.id}
                        className={[
                          styles.valueItem,
                          itemHighlighted ? styles.valueItemHighlighted : "",
                        ].join(" ")}
                      >
                        <span
                          className={
                            item.type === "landmark"
                              ? styles.itemTypeLandmark
                              : styles.itemTypeFood
                          }
                        >
                          {item.type === "landmark" ? "🏛" : "🍜"}
                        </span>
                        <span>{item.label}</span>
                        <button
                          type="button"
                          className={styles.itemDeleteBtn}
                          onClick={() => onDeleteItem(entry.id, item.id)}
                        >
                          ✕
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SimpleHashMapEngine;
